import { artistService } from './artist';
import { albumsService } from './albums';
import { querySearch } from './search';
import { supabase } from '../lib/supabase';
import { extractSpotifyArtistId } from '../lib/spotifyArtistUrl';
import type {
  SpotifyProfile,
  SpotifyCatalogAlbum,
  SpotifyCatalogTrack,
} from '../interfaces/maestra';

// Constrói o perfil + catálogo do artista a partir da Spotify Web API.
// Observação: a API (estado Feb/2026) removeu followers, popularity e /top-tracks do
// Development Mode. Usamos a Edge Function `spotify-artist-stats` como fallback para obter
// seguidores e popularidade via scraping da página pública do Spotify.

export interface SpotifyArtistSearchResult {
  id: string;
  name: string;
  image?: string;
  followers?: number;
  genres?: string[];
}

const mapearArtista = (a: any): SpotifyArtistSearchResult => ({
  id: a.id,
  name: a.name,
  image: a.images?.[a.images.length - 1]?.url || a.images?.[0]?.url,
  followers: a.followers?.total,
  genres: a.genres,
});

/** Busca direta no Spotify a partir do navegador. Caminho legado, mantido só como rede de
 *  segurança do proxy (ver `searchSpotifyArtists`). Quando veio um link colado, busca pelo ID —
 *  procurar a URL como se fosse nome não acharia nada. */
const buscaDiretaNoSpotify = async (
  query: string,
  artistId: string | null
): Promise<SpotifyArtistSearchResult[]> => {
  if (artistId) {
    const { data } = await artistService.fetchArtist(artistId);
    return data ? [mapearArtista(data)] : [];
  }
  const { data } = await querySearch({ q: query, type: 'artist', limit: 8 });
  return (data.artists?.items || []).map(mapearArtista);
};

/**
 * Busca artistas por nome (tela de criação de perfil).
 *
 * Passa pela Edge Function `spotify-artist-search` em vez de falar direto com o Spotify. O motivo
 * é a cota: o limite do Spotify é POR APLICATIVO numa janela de 30s e a Maestra usa
 * client_credentials, então TODOS os usuários dividem o mesmo balde. Indo pelo proxy ganhamos
 * cache compartilhado (o segundo que buscar o mesmo termo custa zero), ritmo global e cache
 * vencido como rede de segurança quando o Spotify está fora. Ver docs/RISCO-SPOTIFY-ESCALA.md.
 *
 * O fallback para a busca direta existe por causa da ORDEM DE SUBIDA: o frontend (Vercel) sobe no
 * merge, a edge function é deployada à mão. Sem ele, a janela entre os dois deploys deixaria a
 * busca quebrada para todo mundo. Depois que o proxy estiver em produção e estável, dá para
 * remover o fallback junto com o token do Spotify no navegador (src/lib/spotifyToken.ts).
 */
export const searchSpotifyArtists = async (
  query: string
): Promise<SpotifyArtistSearchResult[]> => {
  const termo = query.trim();
  if (termo.length < 3) return [];

  // Link/URI/ID colado: vai direto no artista em vez de procurar por nome. Nome curto ou comum
  // ("BEA") afunda na ordenação por relevância do Spotify e não aparece na lista — colar o link
  // do perfil é o caminho determinístico. Ver src/lib/spotifyArtistUrl.ts.
  const artistId = extractSpotifyArtistId(termo);

  try {
    const { data, error } = await supabase.functions.invoke('spotify-artist-search', {
      body: artistId ? { id: artistId } : { q: termo },
    });
    if (error) throw error;
    if (Array.isArray(data?.results)) return data.results as SpotifyArtistSearchResult[];
    throw new Error('resposta inesperada do proxy de busca');
  } catch (e: any) {
    // 403/429 são decisões do Spotify: repetir direto do navegador não muda o veredito e ainda
    // queima cota. Só caímos no caminho legado quando o problema é o PROXY (ex.: function ainda
    // não deployada), nunca quando o proxy funcionou e o Spotify é que recusou.
    const status = e?.context?.status ?? e?.status;
    if (status === 403 || status === 429 || status === 503) throw e;
    console.warn('[Spotify] proxy de busca indisponível, usando chamada direta:', e?.message || e);
    return buscaDiretaNoSpotify(termo, artistId);
  }
};

/**
 * Busca followers e popularity via token do embed player do Spotify.
 * Necessário porque o Dev Mode da Web API removeu esses campos em Feb/2026.
 *
 * Estratégia:
 * 1. Pede à Edge Function um token anônimo do embed player (Extended Quota)
 * 2. Usa esse token client-side para chamar a API e obter followers/popularity
 *
 * O token do embed pertence ao web player oficial (Extended Quota) e retorna todos os campos.
 * A chamada à API é feita client-side pois IPs de datacenter são bloqueados pelo Spotify.
 */
const fetchArtistStats = async (
  artistId: string
): Promise<{ followers?: number; popularity?: number }> => {
  try {
    // Pedir token anônimo via Edge Function (que busca do embed page)
    const { data: tokenData, error: tokenError } = await supabase.functions.invoke(
      'spotify-artist-stats',
      { body: { artist_id: artistId, action: 'get_token' } }
    );

    const embedToken = tokenError ? null : tokenData?.embed_token;

    if (embedToken) {
      // Usar o token client-side para chamar a API (não passa pelo axios cache/interceptors)
      const resp = await fetch(`https://api.spotify.com/v1/artists/${artistId}`, {
        headers: { Authorization: `Bearer ${embedToken}` },
      });
      if (resp.ok) {
        const artist = await resp.json();
        const result: { followers?: number; popularity?: number } = {};
        if (typeof artist.followers?.total === 'number') result.followers = artist.followers.total;
        if (typeof artist.popularity === 'number') result.popularity = artist.popularity;
        if (result.followers != null || result.popularity != null) return result;
      }
    }

    // Fallback: chamar a Edge Function para tentar server-side (pode falhar por IP)
    const { data, error } = await supabase.functions.invoke('spotify-artist-stats', {
      body: { artist_id: artistId },
    });
    if (!error && data) {
      const result: { followers?: number; popularity?: number } = {};
      if (typeof data.followers === 'number') result.followers = data.followers;
      if (typeof data.popularity === 'number') result.popularity = data.popularity;
      if (result.followers != null || result.popularity != null) return result;
    }
  } catch {
    // Tudo falhou
  }

  return {};
};

const MAX_ALBUMS = 10;
const MAX_ALBUMS_FOR_TRACKS = 8;

export const buildSpotifyProfileAndCatalog = async (
  spotifyArtistId: string
): Promise<{
  profile: SpotifyProfile;
  catalog: { albums: SpotifyCatalogAlbum[]; tracks: SpotifyCatalogTrack[] };
}> => {
  const { data: artist } = await artistService.fetchArtist(spotifyArtistId);

  const { data: albumsPage } = await artistService.fetchArtistAlbums(spotifyArtistId, {
    include_groups: 'album',
    limit: MAX_ALBUMS,
  });

  // Tenta também singles (endpoint aceita um grupo por vez na prática); ignora erros.
  let singles: any[] = [];
  try {
    const res = await artistService.fetchArtistAlbums(spotifyArtistId, {
      include_groups: 'single',
      limit: MAX_ALBUMS,
    });
    singles = res.data.items || [];
  } catch {
    singles = [];
  }

  const rawAlbums = [...(albumsPage.items || []), ...singles];

  // Dedupe por id
  const seen = new Set<string>();
  const albums: SpotifyCatalogAlbum[] = [];
  for (const al of rawAlbums) {
    if (seen.has(al.id)) continue;
    seen.add(al.id);
    albums.push({
      id: al.id,
      name: al.name,
      image: al.images?.[0]?.url,
      release_date: al.release_date,
      total_tracks: al.total_tracks,
      spotify_url: al.external_urls?.spotify,
    });
  }

  // Faixas dos primeiros álbuns (respeitando rate limit)
  const tracks: SpotifyCatalogTrack[] = [];
  const trackSeen = new Set<string>();
  for (const al of albums.slice(0, MAX_ALBUMS_FOR_TRACKS)) {
    try {
      const { data: tracksPage } = await albumsService.fetchAlbumTracks(al.id, { limit: 20 });
      for (const t of tracksPage.items || []) {
        const key = t.name.toLowerCase();
        if (trackSeen.has(key)) continue;
        trackSeen.add(key);
        tracks.push({
          id: t.id,
          name: t.name,
          album: al.name,
          album_image: al.image,
          duration_ms: t.duration_ms,
          preview_url: (t as any).preview_url ?? null,
          spotify_url: t.external_urls?.spotify,
        });
      }
    } catch {
      // ignora álbum que falhar
    }
  }

  const profile: SpotifyProfile = {
    spotify_artist_id: artist.id,
    name: artist.name,
    image: artist.images?.[0]?.url,
    followers: artist.followers?.total,
    popularity: artist.popularity,
    genres: artist.genres,
    track_count: tracks.length,
    fetched_at: new Date().toISOString(),
  };

  // Desde Feb/2026, Dev Mode não retorna followers/popularity na Web API.
  // Usa a Edge Function como fallback para buscar esses dados via scraping.
  if (profile.followers == null || profile.popularity == null) {
    const stats = await fetchArtistStats(spotifyArtistId);
    if (stats.followers != null && profile.followers == null) {
      profile.followers = stats.followers;
    }
    if (stats.popularity != null && profile.popularity == null) {
      profile.popularity = stats.popularity;
    }
  }

  return { profile, catalog: { albums, tracks } };
};
